<script setup lang="ts">
import DOMPurify from 'dompurify'

import { ref, watch } from 'vue'

import { withoutPendingMath } from '../../composables/chat-math'
import { useMarkdown } from '../../composables/markdown'

interface Props {
  content: string
  class?: string | string[]
  /** True while the message streams. A formula that is still open is not rendered, so it does not flash as an error. */
  streaming?: boolean
}

const props = defineProps<Props>()

const processedContent = ref('')
const { process, processSync } = useMarkdown()
let processRequestId = 0

async function processRichContent(content: string, requestId: number) {
  try {
    const result = DOMPurify.sanitize(await process(content))
    if (requestId === processRequestId)
      processedContent.value = result
  }
  catch (error) {
    console.warn('Failed to process markdown with syntax highlighting, using fallback:', error)
  }
}

function processContent() {
  const content = props.streaming ? withoutPendingMath(props.content) : props.content
  const requestId = ++processRequestId

  if (!content) {
    processedContent.value = ''
    return
  }

  try {
    processedContent.value = DOMPurify.sanitize(processSync(content))
  }
  catch (error) {
    console.warn('Failed to process markdown:', error)
    processedContent.value = ''
    return
  }

  if (/`{3,}|~{3,}/.test(content))
    void processRichContent(content, requestId)
}

watch(() => [props.content, props.streaming], processContent, { immediate: true })
</script>

<template>
  <div
    :class="props.class"
    class="markdown-content"
    v-html="processedContent"
  />
</template>

<style scoped>
.markdown-content :deep(pre) {
  overflow-x: auto;
  max-width: 100%;
  border-radius: 6px;
  padding: 1rem;
  margin: 0.5rem 0;
}

.markdown-content :deep(code) {
  font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
  font-size: 0.875em;
}

.markdown-content :deep(pre code) {
  display: block;
  width: fit-content;
  min-width: 100%;
}

/* Ensure horizontal scrolling for wide code blocks */
.markdown-content :deep(pre.shiki) {
  overflow-x: auto;
  white-space: pre;
}

.markdown-content :deep(.shiki) {
  border-radius: 6px;
  padding: 1rem;
  margin: 0.5rem 0;
}

/* Fallback styles for non-shiki code blocks */
.markdown-content :deep(pre:not(.shiki)) {
  background: #f6f8fa;
  border: 1px solid #d0d7de;
}

.dark .markdown-content :deep(pre:not(.shiki)) {
  background: #161b22;
  border: 1px solid #30363d;
}

/* Force Shiki multi-theme output to follow our dark mode */
.dark .markdown-content :deep(.shiki) {
  background: var(--shiki-dark-bg, #0d1117) !important;
  color: var(--shiki-dark, #e6edf3) !important;
}

.dark .markdown-content :deep(.shiki span[style*="--shiki-dark"]) {
  color: var(--shiki-dark, inherit) !important;
}

.dark .markdown-content :deep(.shiki span[style*="--shiki-dark-background"]) {
  background-color: var(--shiki-dark-background, var(--shiki-dark-bg, transparent)) !important;
}
</style>
